import React, { useState, useEffect, useCallback, useContext } from 'react';

import { Check as CheckIcon } from 'lucide-react';
import { api } from 'services/api';
import { formatDate } from 'shared/date';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  HStack,
  Flex,
  Input,
  Select,
  Button,
  Icon,
  Text,
  Tooltip,
  VStack,
  Box,
} from '@chakra-ui/react';

import construmanagerSymbol from 'assets/img/construmanager-symbol.ico';
import ecustosSymbol from 'assets/img/ecustos-symbol.ico';
import siengeSymbol from 'assets/img/sienge-symbol.ico';

import AppTable from 'components/AppTable';
import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import AuthContext from 'contexts/AuthContext';

import useDebouncedState from 'hooks/useDebouncedState';
import useThrottledState from 'hooks/useThrottledState';

import { Budget } from 'types/budget';
import { Pagination, ServicePagination } from 'types/pagination';

interface ModalBudgetLookupProps extends ModalRootProps {
  data?: {
    isSelectingForReuse?: boolean;
    targetBudgetId?: number;
  };
}

const ModalBudgetLookup: React.FC<ModalBudgetLookupProps> = ({
  data,
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const { user } = useContext(AuthContext);
  const { isSelectingForReuse = false, targetBudgetId } = data || {};

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(false);

  const [keyword, setKeyword] = useDebouncedState<string>('');
  const [sort, setSort] = useState('-created_at');
  const [pagination, setPagination] = useThrottledState<Pagination>(
    {
      per_page: 10,
      page: 1,
    },
    1000,
  );

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getBudgetList = useCallback(async () => {
    setLoading(true);
    setBudgets([]);

    try {
      const params: Record<string, string | number> = {
        'filter[keyword]': keyword,
        'filter[status]': 'active',
        'filter[organization_id]': user?.managed_organization?.organization?.id,
        page: pagination.page,
        per_page: pagination.per_page,
        sort,
      };

      if (isSelectingForReuse && targetBudgetId) {
        params['filter[destination_budget_id]'] = targetBudgetId;
      }

      const response = await api.get('/budget', {
        params,
      });

      const responseData = response.data;

      setBudgets(responseData.data || []);
      setServicePagination({
        last_page: responseData.meta?.last_page || 1,
      });
    } catch {
      setBudgets([]);
      setServicePagination({ last_page: 1 });
    } finally {
      setLoading(false);
    }
  }, [
    isSelectingForReuse,
    keyword,
    pagination.page,
    pagination.per_page,
    sort,
    targetBudgetId,
    user,
  ]);

  useEffect(() => {
    getBudgetList();
  }, [getBudgetList]);

  useEffect(() => {
    if (pagination.page > servicePagination.last_page) {
      setPagination((oldPagination) => {
        if (oldPagination.page > 1) {
          return {
            ...oldPagination,
            page: 1,
          };
        }

        return oldPagination;
      });
    }
  }, [pagination.page, servicePagination.last_page, setPagination]);

  const handleConfirm = (budget: Budget): void => {
    if (onConfirm) onConfirm(budget);
    handleClose();
  };

  return (
    <Modal {...restProps} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Selecionar orçamento</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} mt={6}>
            <HStack mb={3}>
              <Text fontSize="smaller">Por página:</Text>
              <Select
                width="auto"
                defaultValue={pagination.per_page}
                onChange={(e) => {
                  setPagination({
                    ...pagination,
                    per_page: Number(e.target.value),
                  });
                }}
              >
                {[5, 10, 20, 50, 100].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </HStack>

            <Flex
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              mb={3}
              gridGap={{ base: 0, md: 3 }}
            >
              <Input
                type="text"
                placeholder="Pesquisar"
                defaultValue={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                mb={{ base: 3, md: 0 }}
              />
            </Flex>
          </SimpleGrid>

          {isSelectingForReuse && targetBudgetId && (
            <Box
              w="100%"
              border="1px solid"
              borderColor="gray.100"
              borderLeftWidth="3px"
              borderLeftColor="gray.300"
              borderRadius="8px"
              background="gray.50"
              p={3}
              mb={4}
            >
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                Orçamentos disponíveis
              </Text>

              <Box as="ul" pl={5} color="gray.700" fontSize="sm">
                <Text as="li" whiteSpace="pre-wrap">
                  Para importar itens de outro orçamento, os dois orçamentos
                  precisam compartilhar as mesmas configurações de base
                  (referência, estado e tipo de preço).
                </Text>
                <Text as="li" mt={1} whiteSpace="pre-wrap">
                  Nesta listagem, você verá apenas orçamentos compatíveis com o
                  orçamento de destino.
                </Text>
              </Box>
            </Box>
          )}

          <AppTable
            cols={[
              { field: 'type', description: 'Tipo', sortable: false },
              { field: 'created_at', description: 'Criação' },
              {
                field: 'description',
                description: 'Descrição',
                sortable: false,
              },
              { field: 'total', description: 'Total', isNumeric: true },
              { field: 'actions', description: 'Ações', sortable: false },
            ]}
            mapping={(item) => ({
              id: item.id,
              type: (
                <VStack
                  align={{ base: 'flex-start', md: 'center' }}
                  spacing={1}
                >
                  {item.integrated_construmanager && (
                    <Tooltip
                      label="Orçamento integrado com o Construmanager"
                      hasArrow
                    >
                      <img
                        src={construmanagerSymbol}
                        alt="Construmanager"
                        width="16"
                        height="16"
                      />
                    </Tooltip>
                  )}

                  {item.integrated_sienge ? (
                    <Tooltip label="Orçamento integrado com o Sienge" hasArrow>
                      <img
                        src={siengeSymbol}
                        alt="Sienge"
                        width="16"
                        height="16"
                      />
                    </Tooltip>
                  ) : (
                    <img
                      src={ecustosSymbol}
                      alt="Ecustos"
                      width="16"
                      height="16"
                    />
                  )}
                </VStack>
              ),
              created_at: <Text mb={1}>{formatDate(item.created_at)}</Text>,
              description: (
                <Text
                  as="span"
                  maxWidth={{ base: '100%', md: '170px', lg: '280px' }}
                  whiteSpace="pre-wrap"
                  fontWeight="bold"
                >
                  {item.identification?.description || '-'}
                </Text>
              ),
              total: Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(item.total || 0),
              actions: (
                <Button
                  colorScheme="green"
                  size="sm"
                  leftIcon={<Icon as={CheckIcon} />}
                  onClick={() => handleConfirm(item)}
                >
                  Selecionar
                </Button>
              ),
            })}
            data={budgets}
            loading={loading}
            onUpdateSort={(newSort) => {
              setSort(newSort || '-created_at');
            }}
          />

          <PaginationWrapper
            lastPage={servicePagination.last_page}
            onPaginate={(selectedPage) => {
              setPagination({ ...pagination, page: selectedPage });
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetLookup;
